import { type Dtos, formatToDollar, formatToPercent } from "@inrev/common";
import { Text } from "@portal/components/primitives";

const ResultRow = ({
	label,
	percent,
	amount,
	testId,
	emphasis = false,
}: {
	label: string;
	percent: string;
	amount?: string | null;
	testId: string;
	emphasis?: boolean;
}) => (
	<div className="flex items-baseline justify-between gap-6 py-2.5">
		<Text type={emphasis ? "body-strong" : "caption"} color={emphasis ? "primary" : "secondary"}>
			{label}
		</Text>
		<div className="flex shrink-0 items-baseline gap-6" data-testid={testId}>
			<Text
				type={emphasis ? "body-strong" : "caption"}
				color={emphasis ? "primary" : "secondary"}
				className="w-[64px] text-right tabular-nums"
			>
				{percent}
			</Text>
			{amount != null && (
				<Text type="body-strong" color="primary" className="w-[104px] text-right tabular-nums">
					{amount}
				</Text>
			)}
		</div>
	</div>
);

export type PelPmlBreakdownProps = {
	result: Dtos.Admin.PelPml.Calculate.Response;
	testIdPrefix?: string;
	showFormulas?: boolean;
};

export const PelPmlBreakdown = ({
	result,
	testIdPrefix = "pel-pml",
	showFormulas = false,
}: PelPmlBreakdownProps) => (
	<div className="flex flex-col gap-5">
		<div className="flex flex-col divide-y divide-bc-default rounded-md border border-bc-default bg-surface-light px-5 py-1">
			<ResultRow
				label="PEL"
				percent={formatToPercent(result.pelPercent, 2)}
				amount={formatToDollar(result.pelAmount, true)}
				testId={`${testIdPrefix}-pel`}
				emphasis
			/>
			<ResultRow
				label="PML at 90% confidence"
				percent={formatToPercent(result.pml90Percent, 2)}
				amount={formatToDollar(result.pml90Amount, true)}
				testId={`${testIdPrefix}-pml90`}
				emphasis
			/>
			<ResultRow
				label="PML at 95% confidence"
				percent={formatToPercent(result.pml95Percent, 2)}
				amount={formatToDollar(result.pml95Amount, true)}
				testId={`${testIdPrefix}-pml95`}
				emphasis
			/>
		</div>

		<div className="flex flex-col gap-2">
			<Text type="eyebrow" color="tertiary">
				Adjustments
			</Text>
			<div className="flex flex-col divide-y divide-bc-default">
				<ResultRow
					label="Concentration ratio"
					percent={formatToPercent(result.concentrationRatio, 1)}
					testId={`${testIdPrefix}-concentration-ratio`}
				/>
				<ResultRow
					label="Base"
					percent={formatToPercent(result.basePercent, 2)}
					testId={`${testIdPrefix}-base-percent`}
				/>
				<ResultRow
					label="Concentration adjustment"
					percent={formatToPercent(result.concentrationPercent, 2)}
					testId={`${testIdPrefix}-concentration-percent`}
				/>
				<ResultRow
					label="Region adjustment"
					percent={formatToPercent(result.regionPercent, 2)}
					testId={`${testIdPrefix}-region-percent`}
				/>
			</div>
		</div>

		{showFormulas ? (
			<div className="flex flex-col gap-2.5 border-t border-bc-default pt-5">
				<Text type="eyebrow" color="tertiary">
					How this is calculated
				</Text>
				<Text type="caption" color="tertiary">
					Concentration ratio = Bond Amount &divide; Total Exposure
				</Text>
				<Text type="caption" color="tertiary">
					PEL % = Base % &times; (1 + Concentration %) &times; (1 + Region %)
				</Text>
				<Text type="caption" color="tertiary">
					PEL $ = PEL % &times; Total Exposure, and each PML scales the PEL to its confidence level.
				</Text>
			</div>
		) : null}
	</div>
);
